'use client';

import * as React from 'react';

import { NavigationMenu as NavigationMenuPrimitive } from '@base-ui/react/navigation-menu';
import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';

import { cn, withBaseClasses } from '@/utils/cn';

const NavigationMenuRootContext = React.createContext<{
  rootRef: React.RefObject<HTMLElement | null>;
  orientation: 'horizontal' | 'vertical';
} | null>(null);

const NavigationMenu = React.forwardRef<HTMLElement, NavigationMenuPrimitive.Root.Props>(
  ({ className, children, delay = 200, closeDelay = 150, orientation = 'horizontal', ...props }, ref) => {
    const rootRef = React.useRef<HTMLElement | null>(null);
    React.useImperativeHandle(ref, () => rootRef.current as HTMLElement);

    const indicatorContext = React.useMemo(() => ({ orientation, rootRef }), [orientation]);

    return (
      <NavigationMenuRootContext.Provider value={indicatorContext}>
        <NavigationMenuPrimitive.Root
          aria-label="Main"
          className={withBaseClasses(['relative z-10 flex max-w-max flex-1 items-center justify-center'], className)}
          closeDelay={closeDelay}
          data-navigation-root=""
          delay={delay}
          orientation={orientation}
          ref={rootRef}
          {...props}
        >
          {children}
          <NavigationMenuViewport anchor={rootRef} />
        </NavigationMenuPrimitive.Root>
      </NavigationMenuRootContext.Provider>
    );
  },
);
NavigationMenu.displayName = 'NavigationMenu';

const NavigationMenuList = React.forwardRef<HTMLUListElement, NavigationMenuPrimitive.List.Props>(
  ({ className, ...props }, ref) => (
    <NavigationMenuPrimitive.List
      className={withBaseClasses(['group flex flex-1 list-none items-center justify-center space-x-1'], className)}
      ref={ref}
      {...props}
    />
  ),
);
NavigationMenuList.displayName = 'NavigationMenuList';

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = cva(
  'group inline-flex w-max items-center justify-center font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50',
);

const NavigationMenuTrigger = React.forwardRef<HTMLButtonElement, NavigationMenuPrimitive.Trigger.Props>(
  ({ className, children, ...props }, ref) => (
    <NavigationMenuPrimitive.Trigger
      className={withBaseClasses([navigationMenuTriggerStyle()], className)}
      data-navigation-trigger=""
      ref={ref}
      {...props}
    >
      {children}{' '}
      <ChevronDown
        aria-hidden="true"
        className="relative top-[1px] ml-1 h-3 w-3 transition duration-300 group-data-popup-open:rotate-180"
      />
    </NavigationMenuPrimitive.Trigger>
  ),
);
NavigationMenuTrigger.displayName = 'NavigationMenuTrigger';

// The old viewport measured content without its own 1px borders. Compensate
// on the trailing edges so popup and link geometry retain those dimensions.
const contentStyle =
  '-mr-0.5 -mb-0.5 top-0 left-0 w-full transition-[opacity,translate] duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0 data-[activation-direction=left]:data-ending-style:translate-x-1/2 data-[activation-direction=right]:data-ending-style:-translate-x-1/2 data-[activation-direction=left]:data-starting-style:-translate-x-1/2 data-[activation-direction=right]:data-starting-style:translate-x-1/2 md:w-auto';

const NavigationMenuContent = React.forwardRef<HTMLDivElement, NavigationMenuPrimitive.Content.Props>(
  ({ className, ...props }, ref) => (
    <NavigationMenuPrimitive.Content className={withBaseClasses([contentStyle], className)} ref={ref} {...props} />
  ),
);
NavigationMenuContent.displayName = 'NavigationMenuContent';

const NavigationMenuLink = React.forwardRef<HTMLAnchorElement, NavigationMenuPrimitive.Link.Props>(
  ({ closeOnClick = true, ...props }, ref) => (
    <NavigationMenuPrimitive.Link closeOnClick={closeOnClick} ref={ref} {...props} />
  ),
);
NavigationMenuLink.displayName = 'NavigationMenuLink';

const NavigationMenuViewport = React.forwardRef<
  HTMLDivElement,
  NavigationMenuPrimitive.Viewport.Props & Pick<NavigationMenuPrimitive.Positioner.Props, 'anchor'>
>(({ className, anchor, ...props }, ref) => (
  <NavigationMenuPrimitive.Portal>
    <NavigationMenuPrimitive.Positioner
      align="start"
      anchor={anchor}
      className="isolate z-50"
      collisionPadding={16}
      sideOffset={6}
    >
      <NavigationMenuPrimitive.Popup className="relative h-(--popup-height) w-(--popup-width) origin-top overflow-clip rounded-md border border-black/10 bg-white text-black shadow-lg transition-[width,height,opacity,scale] duration-200 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-90 data-starting-style:opacity-0">
        <NavigationMenuPrimitive.Viewport className={className} ref={ref} {...props} />
      </NavigationMenuPrimitive.Popup>
    </NavigationMenuPrimitive.Positioner>
  </NavigationMenuPrimitive.Portal>
));
NavigationMenuViewport.displayName = 'NavigationMenuViewport';

// Observe the target primitive's public open state without intercepting its
// controlled/default value or cancelable change events. The popup still anchors
// to the whole row; this decoration follows the actual active trigger instead.
const NavigationMenuIndicator = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, style, ...props }, ref) => {
    const context = React.useContext(NavigationMenuRootContext);
    const indicatorRef = React.useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = React.useState<{ size: number; offset: number } | null>(null);
    React.useImperativeHandle(ref, () => indicatorRef.current as HTMLDivElement);

    React.useEffect(() => {
      const root = context?.rootRef.current;
      const indicator = indicatorRef.current;
      if (!root || !indicator) return;
      const horizontal = context.orientation === 'horizontal';
      const selector = '[data-navigation-trigger]';

      const measure = () => {
        const trigger = Array.from(root.querySelectorAll<HTMLElement>(`${selector}[data-popup-open]`)).find(
          (element) => element.closest('[data-navigation-root]') === root,
        );
        if (!trigger) {
          setPosition(null);
          return;
        }
        const track = indicator.offsetParent instanceof HTMLElement ? indicator.offsetParent : root;
        const triggerRect = trigger.getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        const next = {
          offset: horizontal
            ? triggerRect.left - trackRect.left - track.clientLeft + track.scrollLeft
            : triggerRect.top - trackRect.top - track.clientTop + track.scrollTop,
          size: horizontal ? triggerRect.width : triggerRect.height,
        };
        setPosition((previous) => (previous?.size === next.size && previous.offset === next.offset ? previous : next));
      };
      const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
      const observeSizes = () => {
        resize?.disconnect();
        resize?.observe(root);
        for (const trigger of root.querySelectorAll(selector)) resize?.observe(trigger);
        if (indicator.offsetParent instanceof HTMLElement && indicator.offsetParent !== root) {
          resize?.observe(indicator.offsetParent);
        }
      };
      const mutations = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'childList')) observeSizes();
        measure();
      });
      mutations.observe(root, {
        attributeFilter: ['data-popup-open'],
        attributes: true,
        childList: true,
        subtree: true,
      });
      observeSizes();
      root.ownerDocument.defaultView?.addEventListener('resize', measure);
      measure();
      return () => {
        mutations.disconnect();
        resize?.disconnect();
        root.ownerDocument.defaultView?.removeEventListener('resize', measure);
      };
    }, [context]);

    const horizontal = context?.orientation !== 'vertical';
    return (
      <div
        {...props}
        aria-hidden="true"
        className={cn('top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden', className)}
        data-closed={position ? undefined : ''}
        data-open={position ? '' : undefined}
        hidden={!position}
        ref={indicatorRef}
        style={{
          position: 'absolute',
          ...(horizontal
            ? { left: position?.offset, width: position?.size }
            : { height: position?.size, top: position?.offset }),
          ...style,
          display: position ? style?.display : 'none',
        }}
      >
        <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-black/10" />
      </div>
    );
  },
);
NavigationMenuIndicator.displayName = 'NavigationMenuIndicator';

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
